import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import {
  ArrowLeft, Laptop, Package, Plus, Trash2, CheckCircle2, XCircle, Send,
  Loader2, ClipboardList, Users, Wrench, HardDrive, MonitorSmartphone,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_ICONS = {
  laptop: Laptop, phone: MonitorSmartphone, tablet: MonitorSmartphone,
  monitor: HardDrive, headset: Package, other: Package,
};
const STATUS_COLOR = {
  available: 'bg-emerald-100 text-emerald-700',
  assigned: 'bg-blue-100 text-blue-700',
  in_repair: 'bg-amber-100 text-amber-700',
  retired: 'bg-slate-200 text-slate-600',
};
const REQ_STATUS_COLOR = {
  pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-blue-100 text-blue-700',
  denied: 'bg-red-100 text-red-700',
  fulfilled: 'bg-emerald-100 text-emerald-700',
};

const HRInventoryPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const [tab, setTab] = useState('devices');
  const [devices, setDevices] = useState([]);
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ asset_tag: '', category: 'laptop', model: '', serial_number: '', condition: 'new', notes: '' });
  const [fulfillRequest, setFulfillRequest] = useState(null);
  const [selectedDeviceForFulfill, setSelectedDeviceForFulfill] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, r, s] = await Promise.all([
        api.get('/hr/devices'),
        api.get('/hr/device-requests'),
        api.get('/hr/inventory-summary'),
      ]);
      setDevices(d.data.devices || []);
      setRequests(r.data.requests || []);
      setSummary(s.data);
    } catch (err) {
      toast.error('Could not load inventory');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const addDevice = async () => {
    if (!form.asset_tag || !form.model) { toast.error('Asset tag and model required'); return; }
    try {
      await api.post('/hr/devices', form);
      setAddOpen(false);
      setForm({ asset_tag: '', category: 'laptop', model: '', serial_number: '', condition: 'new', notes: '' });
      toast.success('Device added');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not add device');
    }
  };

  const deleteDevice = async (id) => {
    if (!window.confirm('Delete this device permanently?')) return;
    try {
      await api.delete(`/hr/devices/${id}`);
      toast.success('Device removed');
      load();
    } catch (err) {
      toast.error('Could not delete device');
    }
  };

  const decideRequest = async (id, action) => {
    const notes = window.prompt(`${action === 'approve' ? 'Approve' : 'Deny'} — notes for the employee (optional):`, '');
    if (notes === null) return;
    try {
      await api.put(`/hr/device-requests/${id}/decision`, { action, decision_notes: notes });
      toast.success(`Request ${action}d`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update request');
    }
  };

  const openFulfill = (req) => { setFulfillRequest(req); setSelectedDeviceForFulfill(''); };
  const doFulfill = async () => {
    if (!selectedDeviceForFulfill) { toast.error('Pick a device to assign'); return; }
    try {
      await api.post(`/hr/device-requests/${fulfillRequest.id}/fulfill`, { device_id: selectedDeviceForFulfill });
      toast.success('Request fulfilled');
      setFulfillRequest(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not fulfill');
    }
  };

  const availableForCategory = (cat) => devices.filter(d => d.status === 'available' && d.category === cat);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 py-4 flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')} data-testid="hr-inv-back">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <Link to="/admin" className="inline-block hover:opacity-70 transition-opacity" data-testid="hr-inv-title-link">
              <h1 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2">
                <ClipboardList className="w-6 h-6 text-indigo-600" /> HR Inventory & Requests
              </h1>
            </Link>
            <p className="text-sm text-slate-500">Track company equipment and process employee equipment requests.</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6 space-y-6">
        {summary && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="hr-inv-summary">
            <SummaryTile label="Devices" value={summary.devices.total} icon={Laptop} color="text-slate-700" />
            <SummaryTile label="Available" value={summary.devices.available} icon={CheckCircle2} color="text-emerald-600" />
            <SummaryTile label="Assigned" value={summary.devices.assigned} icon={Users} color="text-blue-600" />
            <SummaryTile label="Pending requests" value={summary.requests.pending} icon={ClipboardList} color="text-amber-600" />
          </div>
        )}

        <Tabs value={tab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="devices" data-testid="hr-inv-tab-devices">Inventory ({devices.length})</TabsTrigger>
            <TabsTrigger value="requests" data-testid="hr-inv-tab-requests">Requests ({requests.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="devices" className="mt-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-bold text-slate-800">All devices</h2>
              <Dialog open={addOpen} onOpenChange={setAddOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="hr-inv-add-device"><Plus className="w-4 h-4 mr-1" /> Add device</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add device to inventory</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <Input placeholder="Asset tag (e.g. ZZ-LAP-001)" value={form.asset_tag} onChange={(e) => setForm({...form, asset_tag: e.target.value})} data-testid="hr-inv-form-asset-tag" />
                    <select className="w-full border rounded-md px-3 py-2" value={form.category} onChange={(e) => setForm({...form, category: e.target.value})} data-testid="hr-inv-form-category">
                      <option value="laptop">Laptop</option><option value="phone">Phone</option>
                      <option value="tablet">Tablet</option><option value="monitor">Monitor</option>
                      <option value="headset">Headset</option><option value="other">Other</option>
                    </select>
                    <Input placeholder="Model (e.g. MacBook Pro 14&quot; M3)" value={form.model} onChange={(e) => setForm({...form, model: e.target.value})} data-testid="hr-inv-form-model" />
                    <Input placeholder="Serial number (optional)" value={form.serial_number} onChange={(e) => setForm({...form, serial_number: e.target.value})} />
                    <select className="w-full border rounded-md px-3 py-2" value={form.condition} onChange={(e) => setForm({...form, condition: e.target.value})}>
                      <option value="new">New</option><option value="good">Good</option>
                      <option value="fair">Fair</option><option value="retired">Retired</option>
                    </select>
                    <Textarea placeholder="Notes (optional)" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})} />
                    <Button className="w-full" onClick={addDevice} data-testid="hr-inv-form-submit">Save device</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="grid gap-3" data-testid="hr-inv-device-list">
                {devices.length === 0 && (
                  <Card><CardContent className="p-8 text-center text-slate-500">No devices yet. Add one to start tracking inventory.</CardContent></Card>
                )}
                {devices.map((d) => {
                  const Icon = CATEGORY_ICONS[d.category] || Package;
                  return (
                    <Card key={d.id} data-testid={`hr-inv-device-${d.id}`}>
                      <CardContent className="p-4 flex items-center gap-4">
                        <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                          <Icon className="w-5 h-5 text-slate-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-800 truncate">{d.model} <span className="text-xs text-slate-400 font-normal">· {d.asset_tag}</span></p>
                          <p className="text-xs text-slate-500 truncate">
                            {d.category}
                            {d.serial_number ? ` · S/N ${d.serial_number}` : ''}
                            {d.assigned_to ? ` · Assigned to ${d.assigned_to}` : ''}
                            {d.notes ? ` · ${d.notes}` : ''}
                          </p>
                        </div>
                        <Badge className={STATUS_COLOR[d.status] || ''}>{d.status}</Badge>
                        <Button variant="ghost" size="icon" onClick={() => deleteDevice(d.id)} data-testid={`hr-inv-device-delete-${d.id}`}>
                          <Trash2 className="w-4 h-4 text-slate-400 hover:text-red-500" />
                        </Button>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          <TabsContent value="requests" className="mt-4">
            <h2 className="font-bold text-slate-800 mb-4">Equipment requests</h2>
            {loading ? (
              <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
            ) : (
              <div className="grid gap-3" data-testid="hr-inv-request-list">
                {requests.length === 0 && (
                  <Card><CardContent className="p-8 text-center text-slate-500">No equipment requests yet.</CardContent></Card>
                )}
                {requests.map((r) => (
                  <Card key={r.id} data-testid={`hr-inv-request-${r.id}`}>
                    <CardContent className="p-4 space-y-2">
                      <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                          <p className="font-bold text-slate-800">
                            {r.employee_name || r.employee_email} <span className="text-xs text-slate-400 font-normal">· {r.employee_role}</span>
                          </p>
                          <p className="text-xs text-slate-500">Needs: <strong>{r.category}</strong> {r.model_preference && `(${r.model_preference})`} · urgency: {r.urgency}</p>
                          <p className="text-sm text-slate-700 mt-1">{r.reason}</p>
                          {r.decision_notes && <p className="text-xs italic text-slate-500 mt-1">HR note: {r.decision_notes}</p>}
                        </div>
                        <Badge className={REQ_STATUS_COLOR[r.status] || ''}>{r.status}</Badge>
                      </div>
                      {r.status === 'pending' && (
                        <div className="flex gap-2 pt-2">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => decideRequest(r.id, 'approve')} data-testid={`hr-inv-req-approve-${r.id}`}>
                            <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => decideRequest(r.id, 'deny')} data-testid={`hr-inv-req-deny-${r.id}`}>
                            <XCircle className="w-4 h-4 mr-1" /> Deny
                          </Button>
                        </div>
                      )}
                      {r.status === 'approved' && (
                        <Button size="sm" onClick={() => openFulfill(r)} data-testid={`hr-inv-req-fulfill-${r.id}`}>
                          <Wrench className="w-4 h-4 mr-1" /> Assign a device
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Fulfill dialog */}
      <Dialog open={!!fulfillRequest} onOpenChange={(o) => !o && setFulfillRequest(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign a {fulfillRequest?.category} to {fulfillRequest?.employee_name || fulfillRequest?.employee_email}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-600">Available {fulfillRequest?.category}s:</p>
            <select
              className="w-full border rounded-md px-3 py-2"
              value={selectedDeviceForFulfill}
              onChange={(e) => setSelectedDeviceForFulfill(e.target.value)}
              data-testid="hr-inv-fulfill-picker"
            >
              <option value="">— Pick a device —</option>
              {fulfillRequest && availableForCategory(fulfillRequest.category).map((d) => (
                <option key={d.id} value={d.id}>{d.asset_tag} · {d.model}</option>
              ))}
            </select>
            <Button className="w-full" onClick={doFulfill} data-testid="hr-inv-fulfill-submit">
              <Send className="w-4 h-4 mr-1" /> Assign & fulfill
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const SummaryTile = ({ label, value, icon: Icon, color }) => (
  <Card>
    <CardContent className="p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg bg-slate-50 flex items-center justify-center ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-2xl font-extrabold text-slate-900">{value ?? 0}</p>
      </div>
    </CardContent>
  </Card>
);

export default HRInventoryPage;
